import { capitalize } from "lodash";

import Palette from "@/Utils/Colors/palette";
import { Layer, LAYER_TO_DISPLAY_NAME } from "@/Utils/hierarchy";

import { defaultConfigData, FIXED_LAYER_PLACEHOLDERS, SALES_COLORS, SalesColor } from "./constants";
import { Currency } from "./currency";
import { NumberAbbreviator } from "./numberAbbreviator";
import { ColorConfig, ConfigData, SaleTypes } from "./types";

export class Config {
  mapping: ConfigData["mapping"];
  graphs: ConfigData["graphs"];
  dashboard: any;
  colors: Record<SaleTypes, ColorConfig>;
  config: ConfigData["config"];
  placeholders: { value: string; regex: RegExp; newValue: string }[];
  numberAbbreviator: NumberAbbreviator;
  currency: Currency;
  other: any;
  cfo: ConfigData["cfo"];
  showReturnInsteadOfRevenue: boolean;
  revenueLabel: "Revenue" | "Return";

  constructor(numberAbbreviator: NumberAbbreviator, currency: Currency, configData?: ConfigData) {
    const cmsConfig = configData || defaultConfigData;

    const { config, ...otherCMSConfig } = cmsConfig;

    this.config = config;
    this.placeholders = this.getPlaceholders();

    const initialisedConfig = this.recursiveReplace(otherCMSConfig);
    this.mapping = initialisedConfig.mapping;
    this.graphs = initialisedConfig.graphs;
    this.dashboard = initialisedConfig.dashboard;
    this.colors = initialisedConfig.colors;
    this.numberAbbreviator = numberAbbreviator;
    this.currency = currency;
    this.cfo = initialisedConfig.cfo;
    this.showReturnInsteadOfRevenue = config?.showReturnInsteadOfRevenue || false;
    this.revenueLabel = config?.revenueLabel || "Revenue";
  }

  getLayerLabel = (layer: Layer): string => {
    const { marketingLayer } = this.config;

    if (!marketingLayer) {
      return capitalize(LAYER_TO_DISPLAY_NAME[layer] || layer);
    }

    const getLabel = (layer: Layer) => {
      switch (layer) {
        case Layer.Brand:
        case Layer.Portfolio:
          return marketingLayer.upperLayer || LAYER_TO_DISPLAY_NAME[layer] || layer;
        case Layer.Category:
          return marketingLayer.middleLayer || LAYER_TO_DISPLAY_NAME[layer] || layer;
        case Layer.Product:
          return marketingLayer.lowerLayer || LAYER_TO_DISPLAY_NAME[layer] || layer;
        default:
          return LAYER_TO_DISPLAY_NAME[layer] || layer;
      }
    };

    return capitalize(getLabel(layer));
  };

  getPlaceholders = () => {
    return FIXED_LAYER_PLACEHOLDERS.map(([placeholder, layer]) => {
      // Escape special regex characters in placeholder
      const escapedPlaceholder = placeholder.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
      return {
        value: placeholder,
        regex: new RegExp(escapedPlaceholder, "g"),
        newValue: this.getLayerLabel(layer),
      };
    });
  };

  replacePlaceholder = (str: string): string => {
    return this.placeholders.reduce((acc, { regex, newValue }) => {
      // CMS label values are rather short
      // I'm guestimating for now that performing the replacement right away is faster than
      // checking if the string contains the placeholder first
      return acc.replace(regex, newValue);
    }, str);
  };

  // TODO: Test if JSON.stringify is significantly faster than this recursive function
  recursiveReplace = (obj: any): any => {
    if (typeof obj === "string") {
      return this.replacePlaceholder(obj);
    } else if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        obj[i] = this.recursiveReplace(obj[i]);
      }
    } else if (typeof obj === "object" && obj !== null) {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          obj[key] = this.recursiveReplace(obj[key]);
        }
      }
    }
    return obj;
  };

  getSalesColor = (name: string, palette?: Palette): SalesColor => {
    const salesColor = SALES_COLORS[name];
    if (salesColor !== undefined) {
      return {
        ...salesColor,
        mappedName: this.replacePlaceholder(salesColor.mappedName),
      };
    }

    if (!palette) {
      return {
        borderColor: "#000000",
        color: "#000000",
        mappedName: name,
      };
    }
    const { color, border: borderColor } = palette.chooseColor(name);
    return {
      borderColor,
      color,
      mappedName: name,
    };
  };
}

export default Config;
